import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useVerifyEmail } from 'hooks/useEmailVerification';
import { enterCabinetAfterRegistration } from 'utils/completeOtpAuthSession';
import {
  clearVerificationBrowserKey,
  readVerificationBrowserKey,
} from 'utils/podborDraft';
import { Button } from 'ui';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import classes from './register.module.scss';

export const RegistrationSuccess: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const browserKey = readVerificationBrowserKey();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate: verifyEmail, isPending, isSuccess, isError, data } = useVerifyEmail();
  const verifyStarted = useRef(false);

  useEffect(() => {
    if (!token || verifyStarted.current) {
      return;
    }
    verifyStarted.current = true;
    verifyEmail(
      { token, browser_key: browserKey },
      {
        onError: (error) => {
          setErrorMessage(getApiErrorMessage(error, 'Ссылка недействительна или устарела.'));
        },
      },
    );
  }, [token, verifyEmail, browserKey]);

  const openedCabinet = useRef(false);

  useEffect(() => {
    if (!isSuccess || !data || openedCabinet.current) return;
    if (enterCabinetAfterRegistration(data, navigate)) {
      openedCabinet.current = true;
    }
  }, [isSuccess, data, navigate]);

  const handleFinalAction = () => {
    if (isSuccess && data && enterCabinetAfterRegistration(data, navigate)) {
      openedCabinet.current = true;
      return;
    }

    localStorage.removeItem('registration_email');
    localStorage.removeItem('registration_role');
    localStorage.removeItem('registration_student_type');
    clearVerificationBrowserKey();
    navigate(isError ? '/registration/confirm' : '/login');
  };

  const sessionMissing = Boolean(isSuccess && data && data.session_started === false);

  return (
    <section className={classes.wrapper}>
      <main className={`${classes.container} ${classes.centeredContainer}`}>
        <div className={classes.topSection}>
          <div className={classes.badgeIcon}>⭐</div>
          <h1 className={classes.title}>
            {isPending
              ? 'Проверка данных...'
              : isError
                ? 'Ошибка верификации'
                : 'Аккаунт подтвержден!'}
          </h1>

          <div className={classes.infoBox}>
            {isPending && <p className={classes.infoBoxText}>Пожалуйста, подождите...</p>}
            {isError && (
              <div>
                <p className={classes.errorStatusText}>
                  {errorMessage || 'Ссылка недействительна.'}
                </p>
                <p className={classes.hintText}>
                  Запросите письмо повторно на странице подтверждения регистрации.
                </p>
              </div>
            )}
            {isSuccess && (
              <>
                <p className={classes.infoBoxTextWithBottomMargin}>
                  {data?.message || 'Данные подтверждены.'}
                </p>
                <p className={classes.infoBoxText}>
                  {sessionMissing
                    ? 'Почта подтверждена. Чтобы открыть кабинет, войдите.'
                    : 'Аккаунт готов. Открываем кабинет.'}
                </p>
              </>
            )}
          </div>
        </div>

        <div className={classes.bottomSection}>
          <Button
            variant='filled'
            shape='brick'
            color='secondary'
            widthFullSize
            loading={isPending}
            onClick={handleFinalAction}
            className={classes.submitButton}
          >
            {isError ? 'К подтверждению почты' : sessionMissing ? 'Ко входу' : 'Открыть кабинет'}
          </Button>
        </div>
      </main>
    </section>
  );
};
